'use client';

import { Dialog, DialogContent, DialogTitle } from '@ps/ui/atoms/dialog';
import { format } from 'date-fns/format';
import { Check } from 'lucide-react';

import type {
  MeetingSlot,
  MeetingTypeItem,
} from '../hooks/use-available-dates';
import { formatSlotTime } from '../utils/format';

// ─── Component ────────────────────────────────────────────────────────────

export function BookingSuccessModal({
  open,
  selectedDate,
  selectedType,
  selectedSlot,
  onOpenChange,
  onDone,
}: {
  open: boolean;
  selectedDate: Date | null;
  selectedType: MeetingTypeItem | null;
  selectedSlot: MeetingSlot | null;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        // z-[10000]/[10001] so the confirmation sits above the floating echo
        // bot (z-[9999]) when booking is launched from the chat bubble.
        overlayClassName="z-[10000]"
        className="border-border bg-card dark z-[10001] max-w-sm px-6 py-8"
      >
        <DialogTitle className="sr-only">Booking Confirmed</DialogTitle>
        <div className="flex flex-col items-center gap-4">
          <div className="bg-primary/15 flex h-14 w-14 items-center justify-center rounded-full">
            <Check className="text-primary h-7 w-7" />
          </div>
          <div>
            <h3 className="text-foreground text-center text-lg font-semibold">
              🎉 Your session is booked!
            </h3>
            {selectedType && selectedSlot && (
              <div className="text-muted-foreground mt-2 space-y-1 text-center text-sm">
                <p className="text-foreground/80">{selectedType.topic}</p>
                <p>
                  {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}{' '}
                  at {formatSlotTime(selectedSlot.startTime)}
                </p>
                <p>{selectedType.durationMinutes} min</p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onDone}
            className="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-full py-3 text-sm font-semibold transition-colors"
          >
            You're all set!
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
